import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { StandardPage } from 'ui/base/page/standard_page';
import { Modal, ModalStore } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { CalendarInput } from 'ui/standard/input/calendar_input';
import { ClockInput } from 'ui/standard/input/clock_input';

import { PlainButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { DirectionInput } from 'ui/history/direction_input';
import { ActionInput } from 'ui/history/action_input';
import { ProtocolInput } from 'ui/history/protocol_input';
import { SiteInput } from 'ui/history/site_input';
import { DeviceInput } from 'ui/history/device_input';
import { LocationInput } from 'ui/history/location_input';

import { ResultKind } from 'services/base/types';
import { IPangolinDomain } from 'services/pangolin/types';

import Messages from './filter_history_messages';
import baseStyles from './filter_history.module.css';
import { FilterHistoryStore } from './filter_history_store';

import 'flag-icons/css/flag-icons.min.css';


type FilterHistoryStyles = {
  modal: string;
  header: string;
  applyButton: string;
  ruleContainer: string;
  ruleTitle: string;
  labelContainer: string;
  inputLabel: string;
  inputRow: string;
  inputItem: string;
  checkBoxLabel: string;
  checkBoxContainer: string;
};

const themedStyles: ThemedStyles<FilterHistoryStyles> = {
  [Theme.DAYLIGHT]: {
    modal: baseStyles.modal,
    header: baseStyles.header,
    applyButton: baseStyles.daylightApplyButton,
    ruleContainer: baseStyles.daylightRuleContainer,
    ruleTitle: baseStyles.daylightRuleTitle,
    labelContainer: baseStyles.labelContainer,
    inputLabel: baseStyles.daylightInputLabel,
    inputRow: baseStyles.inputRow,
    inputItem: baseStyles.inputItem,
    checkBoxLabel: baseStyles.checkBoxLabel,
    checkBoxContainer: baseStyles.checkBoxContainer,
  },
  [Theme.MIDNIGHT]: {
    modal: baseStyles.modal,
    header: baseStyles.header,
    applyButton: baseStyles.midnightApplyButton,
    ruleContainer: baseStyles.midnightRuleContainer,
    ruleTitle: baseStyles.midnightRuleTitle,
    labelContainer: baseStyles.labelContainer,
    inputLabel: baseStyles.midnightInputLabel,
    inputRow: baseStyles.inputRow,
    inputItem: baseStyles.inputItem,
    checkBoxLabel: baseStyles.checkBoxLabel,
    checkBoxContainer: baseStyles.checkBoxContainer,
  },
};


type DetailsSectionProps = {
  store: FilterHistoryStore;
};
const DetailsSection = observer(({ store }: DetailsSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.startDateLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputItem}>
            <CalendarInput store={store.dateRange.first} />
          </div>
          <div className={styles.inputItem}>
            <ClockInput store={store.timeStart}/>
          </div>
        </div>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.endDateLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputItem}>
            <CalendarInput store={store.dateRange.final} />
          </div>
          <div className={styles.inputItem}>
            <ClockInput store={store.timeEnd}/>
          </div>
        </div>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.directionsLabel()}</span>
        <DirectionInput store={store.directions} placeholder={Messages.directionsPlaceholder()}/>
      </div>
      <ErrorMessage message={store.directionsError}/>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.actionsLabel()}</span>
        <ActionInput store={store.actions} placeholder={Messages.actionsPlaceholder()}/>
      </div>
      <ErrorMessage message={store.actionsError}/>
    </div>
  );
});

type ProtocolsSectionProps = {
  store: FilterHistoryStore;
};
const ProtocolsSection = observer(({ store }: ProtocolsSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.protocolsLabel()}</div>
      <ProtocolInput store={store.protocols} placeholder={Messages.protocolsPlaceholder()}/>
    </div>
  );
});

type SitesSectionProps = {
  store: FilterHistoryStore;
};
const SitesSection = observer(({ store }: SitesSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.sitesLabel()}</div>
      <SiteInput store={store.sites} placeholder={Messages.sitesPlaceholder()}/>
    </div>
  );
});

type DevicesSectionProps = {
  store: FilterHistoryStore;
  domain: IPangolinDomain;
};
const DevicesSection = observer(({ domain, store }: DevicesSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.devicesLabel()}</div>
      <DeviceInput store={store.devices} hosts={domain.hosts} placeholder={Messages.devicesPlaceholder()}/>
    </div>
  );
});

type LocationsSectionProps = {
  store: FilterHistoryStore;
};
const LocationsSection = observer(({ store }: LocationsSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.locationsLabel()}</div>
      <LocationInput store={store.locations} placeholder={Messages.locationsPlaceholder()}/>
    </div>
  );
});

type FilterHistoryMainProps = {
  domain: IPangolinDomain;
  store: FilterHistoryStore;
};
export const FilterHistoryMain = observer(({ domain, store }: FilterHistoryMainProps) => {
  const nav = useHistory();
  const styles = useStyles(themedStyles);

  const onHideModal = React.useCallback(action(() => {
    nav.goBack();
  }), []);

  const onCommit = React.useCallback(action((event: FormSubmitEvent) => {
    event.preventDefault();
    if (store != null) {
      const result = store.handleApply(domain);
      if (result.kind === ResultKind.OK) {
        toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
        nav.goBack();
        return;
      } else {
        defaultToast(ToastID.ERROR_ACTION_GENERIC);
      }
    }
  }), [store, domain]);

  return (
    <StandardPage>
      <form onSubmit={onCommit} noValidate>
        <div className={styles.header}>
          <BackButton onClick={onHideModal}/>
          <PlainButton type='submit'
            className={styles.applyButton}>{Messages.apply()}
          </PlainButton>
        </div>
        <DetailsSection store={store}/>
        <ProtocolsSection store={store}/>
        <SitesSection store={store}/>
        <DevicesSection store={store} domain={domain} />
        <LocationsSection store={store}/>
      </form>
    </StandardPage>
  );
});

type BaseFilterHistoryProps = {
  domain: IPangolinDomain;
  modal: ModalStore;
};
export const BaseFilterHistory = observer(({ domain, modal }: BaseFilterHistoryProps) => {
  const hosts = domain.hosts;
  const filter = domain.historyFilter;

  const visible = modal.visible;

  const [store, setStore] = React.useState(() => (
    visible ? new FilterHistoryStore(filter, hosts) : null
  ));

  React.useEffect(() => {
    if (visible && store === null) {
      setStore(new FilterHistoryStore(filter, hosts));
    } else if (!visible && store !== null) {
      store.clean(); setStore(null);
    }

    return () => {
      if (store !== null) {
        store.clean();
      }
    };
  }, [store, visible, filter, hosts]);

  return store == null ? (
    <StandardPage>
    </StandardPage>
  ) : (
    <FilterHistoryMain
      domain={domain}
      store={store}
    />
  );
});

type FilterHistoryModalProps = {
  domain: IPangolinDomain;
  modal: ModalStore;
};
export const FilterHistoryModal = observer(({ domain, modal }: FilterHistoryModalProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Modal store={modal}>
      <div className={styles.modal}>
        <BaseFilterHistory
          domain={domain}
          modal={modal}
        />
      </div>
    </Modal>
  );
});
